import { supabase } from '@/lib/supabase'
import { transcodeImageToWebP } from '@/lib/imageTranscode'
import type { MessageAttachment } from '@/lib/messageAttachments'

/**
 * Inbox attachments (file-sharing CP1 — upload/signed-URL helper, no UI).
 *
 * A SEPARATE private bucket `inbox-attachments` with WORKSPACE-scoped RLS (the
 * inbox has no channel membership — read access is workspace membership). This
 * is deliberately NOT the team-chat `message-attachments` bucket: that bucket's
 * read policy is membership-scoped, and OR-ing a workspace-scoped policy onto it
 * would widen team-chat file access. Keeping a distinct bucket keeps the two
 * access models independent.
 *
 * Path convention: {workspaceId}/{conversationId}/{uuid}.{ext}
 *   folder[1] = workspace_id (scopes upload AND read — workspace-scoped)
 *   folder[2] = conversation_id
 * Files are reached only via short-lived signed URLs — never a public URL.
 *
 * Sibling of src/lib/messageAttachments.ts (same shape, different bucket); reuses
 * its MessageAttachment entry type so a render component can be shared. CP2 wires
 * the composer + render.
 */

const BUCKET = 'inbox-attachments'

// Must stay a subset of the bucket's allowed_mime_types (see the 20260623280000
// migration). Images + PDF for v1.
export const INBOX_ALLOWED_MIME = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
  'image/heic',
  'application/pdf',
] as const

export const MAX_INBOX_ATTACHMENT_BYTES = 15 * 1024 * 1024 // 15MB — matches the bucket cap
export const INBOX_ATTACHMENT_ACCEPT_ATTR = INBOX_ALLOWED_MIME.join(',')

/** An inbox attachment entry — same shape as a team-chat MessageAttachment
 * (path/name/size/mime/kind), reused so the render component is shared. */
export type InboxAttachment = MessageAttachment

function extFromMime(mime: string): string {
  switch (mime) {
    case 'image/jpeg': return 'jpg'
    case 'image/png': return 'png'
    case 'image/webp': return 'webp'
    case 'image/gif': return 'gif'
    case 'image/heic': return 'heic'
    case 'application/pdf': return 'pdf'
    default: return 'bin'
  }
}

/**
 * Upload a file to the inbox-attachments bucket for a conversation and return the
 * attachment entry (for CP2 to append to the message's `attachments` jsonb).
 * Validates mime + size; transcodes still images to WebP (best-effort — gif and
 * pdf left as-is).
 *
 * No DB write here — the message write happens in CP2. If that later write fails,
 * CP2 should call deleteInboxAttachment(path) to avoid an orphan.
 */
export async function uploadInboxAttachment(
  file: File,
  workspaceId: string,
  conversationId: string,
): Promise<{ data: InboxAttachment | null; error: Error | null }> {
  if (!INBOX_ALLOWED_MIME.includes(file.type as (typeof INBOX_ALLOWED_MIME)[number])) {
    return { data: null, error: new Error(`Unsupported file type: ${file.type || 'unknown'}`) }
  }
  if (file.size > MAX_INBOX_ATTACHMENT_BYTES) {
    return { data: null, error: new Error('File is too large (max 15MB)') }
  }

  const shouldTranscode = file.type === 'image/jpeg' || file.type === 'image/png' || file.type === 'image/heic'
  const uploadFile = shouldTranscode ? await transcodeImageToWebP(file) : file

  const mime = uploadFile.type || file.type
  const ext = extFromMime(mime)
  const path = `${workspaceId}/${conversationId}/${crypto.randomUUID()}.${ext}`

  const { error: upErr } = await supabase.storage
    .from(BUCKET)
    .upload(path, uploadFile, { cacheControl: '300', contentType: mime || undefined })
  if (upErr) return { data: null, error: new Error(`Upload failed: ${upErr.message}`) }

  return {
    data: {
      path,
      name: file.name,
      size: uploadFile.size,
      mime,
      kind: mime.startsWith('image/') ? 'image' : 'file',
    },
    error: null,
  }
}

/** Short-lived signed URL for a stored inbox attachment. */
export async function getInboxAttachmentSignedUrl(
  storagePath: string,
  ttlSeconds = 300,
): Promise<{ url: string | null; error: Error | null }> {
  const { data, error } = await supabase.storage.from(BUCKET).createSignedUrl(storagePath, ttlSeconds)
  if (error || !data?.signedUrl) {
    return { url: null, error: error ?? new Error('No signed URL returned') }
  }
  return { url: data.signedUrl, error: null }
}

/** Remove a stored object — for orphan cleanup if a message write fails (CP2). */
export async function deleteInboxAttachment(storagePath: string): Promise<{ error: Error | null }> {
  const { error } = await supabase.storage.from(BUCKET).remove([storagePath])
  return { error: error ? new Error(error.message) : null }
}
